import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { DialogRef } from '@angular/cdk/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Product, ProductsType } from '@helpers/types/models';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FileInfo } from '@campaign-portal/namespace/common/fileInfo';
import { SimulationService } from '../simulation.service';
import { ProductsService } from '@helpers/repo/products.service';
import {combineLatest, debounceTime, of, shareReplay, startWith, switchMap, tap} from 'rxjs';
import { map } from 'rxjs/operators';
import { eapiEnable } from '../../../app.module';

@Component({
	selector: 'app-create-simulation',
	standalone: false,
	templateUrl: './create-simulation.component.html',
	styleUrl: './create-simulation.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateSimulationComponent implements OnInit {
	readonly errors: { key: string; value: string }[] = [
		{ key: 'mask', value: 'gl.errors.invalid_format' },
		{ key: 'required', value: 'gl.errors.required' },
		{ key: 'element_unique', value: 'gl.errors.element_unique' }
	];

	readonly separator = 'chr(1) ';

	form = new FormGroup({
		ProductId: new FormControl<number | null>(null),
		ProductName: new FormControl<string | null>(null),
		ProductsType: new FormControl<string>('hlr', { nonNullable: true }),
		SenderId: new FormControl<string | null>(null),
		Prefix: new FormControl<string | null>(null, {validators: [Validators.required,]}),
	});
	products: Product[] = [];
	productsNames: string[] = [];

	readonly productsFilter = new FormControl('');
	readonly productsNamesFilter = new FormControl('');
	files: FileInfo[] = [];
	productNamesTypes = ['all', 'hlr', 'sms'];
	readonly noneProduct = {
		product_id: '',
		product_caption: '',
		acc_currency_code: '',
		product_direction: 0,
		product_type: 0
	} as unknown as Product;
	private readonly destroyRef = inject(DestroyRef);


	constructor(
		public readonly productsService: ProductsService,
		public readonly service: SimulationService,
		private readonly dialogRef: DialogRef<boolean>
	) {}

	ngOnInit(): void {
		const productsType$ = this.form.controls.ProductsType.valueChanges.pipe(
			startWith(this.form.controls.ProductsType.value),
			switchMap((value) => {
				if (eapiEnable) {
					return this.productsService.loadNames([value as ProductsType]).pipe(
						map(res => res.Data ?? [])
					);
				}
				return of<string[]>([]);
			}),
			tap(names => {
				if (names.length > 0) {
					this.productsNamesFilter.reset();
				}
			})
		);

		const productsNamesFilter$ = this.productsNamesFilter.valueChanges.pipe(
			startWith(this.productsNamesFilter.value ?? ''),
			map(filterValue => (filterValue ?? '').toLowerCase())
		);

		combineLatest([productsType$, productsNamesFilter$])
			.pipe(
				map(([names, filterValue]) =>
					names.filter(name => name.toLowerCase().includes(filterValue))
				),
				tap(filteredNames => {
					this.productsNames = filteredNames;
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe();

		const products$ = this.productsService.loadProducts(['all']).pipe(
			map(resp => resp.Data ?? []),
			debounceTime(150),
			shareReplay(1)
		);

		const productsFilter$ = this.productsFilter.valueChanges.pipe(
			startWith(this.productsFilter.value ?? ''),
			map(value => (value ?? '').toLowerCase())
		);

		combineLatest([products$, productsFilter$])
			.pipe(
				map(([products, filterValue]) =>
					products.filter(product =>
						product.product_caption
							.toLowerCase()
							.includes(filterValue)
					)
				),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(products => {
				this.products = [this.noneProduct, ...products];
			});
	}

	close = (result?: any): void => {
		this.form.reset();
		this.dialogRef.close(result);
	};

	confirm = (): void => {
		this.service.create({
			productId: this.form.controls.ProductId.value,
			productName: this.form.controls.ProductName.value,
			senderId: this.form.controls.SenderId.value,
			prefix: this.form.controls.Prefix.value
		})
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(resp => {
				if (resp.createdId) {
					this.close(true);
				}
			});
	}

	displayProduct = (
		productId: string | null
	): string => {
		const product = this.productsService.map.get(productId);
		return product
			? `${product.product_caption} (${product.product_id})` : '';
	};
}
